/**
 * Event WebSocket SDK Implementation
 * Provides real-time WebPush event communication with FleetA servers
 */

import { EventConnectionManager } from './ConnectionManager';
import { EventWebSocketEventManager } from './EventManager';
import { getAuthConfigFromStore, getAuthErrorMessage } from '../auth/authUtils';
import { getEventWebSocketUrl } from '../api/endpointUtils';
import {
  type EventWsEvent,
  type EventWsEventHandler,
  type EventConnectionState,
  type WebPushMessage,
  type EventWebSocketSDKOptions
} from './types';

/**
 * Event WebSocket SDK Class
 * Facade pattern: Provides simple interface for WebPush event handling
 */
export class EventWebSocketSDK {
  // Core components
  private connectionManager: EventConnectionManager;
  private eventManager: EventWebSocketEventManager;
  
  // Configuration
  private config: Required<EventWebSocketSDKOptions>;
  
  // Connection state
  private isInitialized: boolean = false;

  /**
   * Create Event WebSocket SDK instance
   * Authentication is automatically handled via AuthStore
   * @param options - SDK configuration options (optional)
   */
  constructor(options: Partial<EventWebSocketSDKOptions> = {}) {
    // Get base URL from API endpoint system with fallback
    const baseUrl = (() => {
      try {
        return getEventWebSocketUrl();
      } catch (error) {
        return 'wss://webpush-websocket-dev.fleeta.io';
      }
    })();
    
    // Create configuration with dynamic base URL
    this.config = {
      baseUrl,
      maxReconnectAttempts: 5,
      reconnectDelay: 1000,
      heartbeatInterval: 30000,
      psnFilters: [],
      enablePsnFiltering: false,
      ...options
    };
    
    // Create event manager
    this.eventManager = new EventWebSocketEventManager({
      enableFiltering: this.config.enablePsnFiltering,
      allowedPsns: new Set(this.config.psnFilters?.map(psn => psn.toUpperCase()) || [])
    });
    
    // Create connection manager with placeholder URL (will be updated with token)
    this.connectionManager = new EventConnectionManager({
      url: this.generateConnectionUrl(),
      maxReconnectAttempts: this.config.maxReconnectAttempts,
      reconnectDelay: this.config.reconnectDelay,
      heartbeatInterval: this.config.heartbeatInterval
    });
    
    // Setup inter-component communication
    this.setupManagerIntegration();
    
  }

  /**
   * Initialize the SDK
   */
  initialize(): void {
    if (this.isInitialized) {
      return;
    }
    
    this.isInitialized = true;
  }

  /**
   * Connect to Event WebSocket server
   * Automatically uses AuthStore authentication
   */
  connect(): void {
    if (!this.isInitialized) {
      throw new Error('SDK not initialized. Call initialize() first.');
    }
    
    // Check authentication
    const authConfig = getAuthConfigFromStore();
    if (!authConfig) {
      const errorMessage = getAuthErrorMessage();
      this.eventManager.emit('error', new Error(errorMessage));
      return;
    }

    // Update connection URL with latest token
    this.updateConnectionUrl();
    
    // Connect
    this.connectionManager.connect();
  }

  /**
   * Disconnect from Event WebSocket server
   */
  disconnect(): void {
    this.connectionManager.disconnect();
  }

  /**
   * Check WebSocket connection status
   */
  isConnected(): boolean {
    return this.connectionManager.isConnected();
  }

  /**
   * Get current connection state
   */
  getConnectionState(): EventConnectionState {
    return this.connectionManager.getState();
  }

  /**
   * Update authentication from AuthStore
   * Call this when AuthStore auth state changes
   */
  updateAuthFromStore(): void {
    const authConfig = getAuthConfigFromStore();
    
    if (authConfig) {
      this.updateConnectionUrl();
      
      // Reconnect if currently connected to refresh authentication
      if (this.isConnected()) {
        this.connectionManager.disconnect();
        
        // Reconnect after brief delay
        setTimeout(() => {
          this.connectionManager.connect();
        }, 1000);
      }
      
    } else {
    }
  }

  /**
   * Add event listener for WebPush events
   * @param event - Event type to listen for
   * @param handler - Event handler function
   */
  on<T = any>(event: EventWsEvent, handler: EventWsEventHandler<T>): void {
    this.eventManager.on(event, handler);
  }

  /**
   * Remove event listener for WebPush events
   * @param event - Event type
   * @param handler - Event handler to remove
   */
  off(event: EventWsEvent, handler: EventWsEventHandler): void {
    this.eventManager.off(event, handler);
  }

  /**
   * Remove all event listeners for a specific event type
   * @param event - Event type (optional - removes all if not specified)
   */
  removeAllListeners(event?: EventWsEvent): void {
    this.eventManager.removeAllListeners(event);
  }

  /**
   * Set PSN filters to receive events only from specific devices
   * @param psns - Array of PSNs to filter by (empty array = receive all)
   */
  setPsnFilters(psns: string[]): void {
    this.eventManager.setPsnFilters(psns);
  }

  /**
   * Add PSN to filter list
   * @param psn - PSN to add to filter
   */
  addPsnFilter(psn: string): void {
    this.eventManager.addPsnFilter(psn);
  }

  /**
   * Remove PSN from filter list
   * @param psn - PSN to remove from filter
   */
  removePsnFilter(psn: string): void {
    this.eventManager.removePsnFilter(psn);
  }

  /**
   * Clear all PSN filters (receive all events)
   */
  clearPsnFilters(): void {
    this.eventManager.clearPsnFilters();
  }

  /**
   * Get current PSN filters
   * @returns Array of current PSN filters
   */
  getPsnFilters(): string[] {
    return this.eventManager.getPsnFilters();
  }

  /**
   * Check if PSN filtering is enabled
   * @returns True if PSN filtering is active
   */
  isPsnFilteringEnabled(): boolean {
    return this.eventManager.isPsnFilteringEnabled();
  }

  /**
   * Set custom PSN extraction function
   * @param extractorFn - Custom function to extract PSN from message text
   */
  setPsnExtractor(extractorFn: (message: string) => string | null): void {
    this.eventManager.setPsnExtractor(extractorFn);
  }

  /**
   * Test PSN extraction on a message (for debugging)
   * @param message - Message text to test
   * @returns Extracted PSN or null
   */
  testPsnExtraction(message: string): string | null {
    return this.eventManager.testPsnExtraction(message);
  }

  /**
   * Get connection statistics
   * @returns Connection statistics and status
   */
  getConnectionStats(): {
    isConnected: boolean;
    state: EventConnectionState;
    hasValidAuth: boolean;
    totalHandlers: number;
    registeredEvents: EventWsEvent[];
    psnFiltering: {
      enabled: boolean;
      activeFilters: string[];
      filterCount: number;
    };
  } {
    const authConfig = getAuthConfigFromStore();
    
    return {
      isConnected: this.isConnected(),
      state: this.getConnectionState(),
      hasValidAuth: !!authConfig,
      totalHandlers: this.eventManager.getTotalHandlerCount(),
      registeredEvents: this.eventManager.getRegisteredEvents(),
      psnFiltering: {
        enabled: this.isPsnFilteringEnabled(),
        activeFilters: this.getPsnFilters(),
        filterCount: this.getPsnFilters().length
      }
    };
  }

  /**
   * Destroy SDK and cleanup all resources
   */
  destroy(): void {
    this.connectionManager.destroy();
    this.eventManager.destroy();
    this.isInitialized = false;
  }

  // ========================
  // PRIVATE METHODS
  // ========================

  /**
   * Generate connection URL with JWT token
   * @returns WebSocket URL with authentication
   */
  private generateConnectionUrl(): string {
    const authConfig = getAuthConfigFromStore();
    
    if (!authConfig || !authConfig.jwtToken) {
      return this.config.baseUrl;
    }
    
    // Add JWT token as query parameter (without "Bearer" prefix)
    const url = `${this.config.baseUrl}?token=${encodeURIComponent(authConfig.jwtToken)}`;
    return url;
  }

  /**
   * Update connection URL with latest authentication
   */
  private updateConnectionUrl(): void {
    const newUrl = this.generateConnectionUrl();
    this.connectionManager.updateConfig({ url: newUrl });
  }

  /**
   * Setup integration between managers
   */
  private setupManagerIntegration(): void {
    // Setup connection manager callbacks
    this.connectionManager.setCallbacks({
      onOpen: () => {
        this.eventManager.emit('open', undefined);
      },
      onClose: (event) => {
        this.eventManager.emit('close', event);
      },
      onError: (error) => {
        this.eventManager.emit('error', error);
      },
      onMessage: (event) => {
        this.handleMessage(event);
      },
      onStateChange: (state) => {
        // State changes are handled internally
      }
    });
  }

  /**
   * Handle incoming WebSocket messages
   * @param event - MessageEvent from WebSocket
   */
  private handleMessage(event: MessageEvent): void {
    let message: WebPushMessage;
    
    try {
      message = JSON.parse(event.data);
    } catch (error) {
      this.eventManager.emit('error', new Error('Invalid message format'));
      return;
    }

    // Emit raw message event first
    this.eventManager.emit('raw', message);

    // Emit general message event
    this.eventManager.emit('message', message);

    // Handle WebPush specific events
    if (message.type === 'webpush') {
      this.eventManager.emit('webpush', message);
    } else {
    }
  }
}